import { useEffect } from "react"

import { v2GetHomeSession } from "@be-miniapp/api"
import { trackJourney } from "@be-miniapp/logging"
import { CACHE_KEYS } from "@be-miniapp/platform"
import { hasMeaningfulHomeSession } from "@be-miniapp/shared/utils/cache-guards"
import { resolveWarmupLocation } from "@be-miniapp/shared/utils/location-policy"
import type { Location } from "@be-miniapp/types"
import { useAggressiveCache } from "@be-miniapp/ui"

import { useClientInfo } from "@/store/client-info"
import { useUserPersistingData } from "@/store/user-persisting-data"
import { useUserStore } from "@/store/user-session"

export const homeSessionsV2QueryKey = {
  all: ["home-sessions"] as const,
  userLocation: (location: Location | any, accessToken: string) =>
    ["home-sessions", location, accessToken] as const,
}

export function useHomeSessionsV2() {
  const location = useUserStore((state) => state.location)
  const accessToken = useUserStore((state) => state.access_token)
  const setLocation = useUserStore((state) => state.setLocation)
  const setClientLocation = useClientInfo((state) => state.setLocation)
  const allowLocationStatus = useUserPersistingData(
    (state) => state.locationPermissionState.status
  )
  const persistedLocation = useUserPersistingData((state) => state.location)

  const hasValidUserLocation =
    Number.isFinite(location?.latitude) && Number.isFinite(location?.longitude)

  const warmupLocation = resolveWarmupLocation({
    userLocation: location,
    persistedLocation,
  })

  useEffect(() => {
    if (hasValidUserLocation) return

    if (
      persistedLocation &&
      Number.isFinite(persistedLocation.latitude) &&
      Number.isFinite(persistedLocation.longitude)
    ) {
      setLocation(persistedLocation.latitude, persistedLocation.longitude)
      setClientLocation(persistedLocation, "manual")
    }
  }, [hasValidUserLocation, persistedLocation, setClientLocation, setLocation])

  const homeEnabled = !!accessToken && allowLocationStatus !== "not-asked"

  return useAggressiveCache({
    cacheKey: CACHE_KEYS.HOME_SESSIONS,
    queryKey: [
      ...homeSessionsV2QueryKey.userLocation(warmupLocation, accessToken!),
    ],
    queryFn: async () => {
      trackJourney("home_load", "start", { stage: "home_sessions_query" })

      try {
        const homeSession = await v2GetHomeSession({})
        trackJourney("home_load", "success", { stage: "home_sessions_query" })
        return homeSession.data
      } catch (error) {
        trackJourney("home_load", "fail", { stage: "home_sessions_query" })
        throw error
      }
    },
    enabled: homeEnabled,
    ttl: 7 * 24 * 60 * 60 * 1000, // 7 days cache for services list (icons)
    staleTime: 10 * 60 * 1000, // 10 minutes stale time
    refetchOnWindowFocus: true,
    refetchOnMount: true, // Restore original refetchOnMount behavior
    shouldCache: hasMeaningfulHomeSession,
  })
}
